import { timezoneHolder as platformTimezoneHolder } from '@atlassian/townsquare-date-utils/timezone-info';
import { createStore, Store, StoreActionApi } from 'react-sweet-state';

import { default as timezoneHolder } from '@townsquare/date-utils/timezone-holder';
import { identityClient, UserResponse } from '@townsquare/identity-client';

import { User } from './types';

/** Keep legacy and platform date-utils holders in sync for AGG/platform components. */
const syncUserTimezoneToHolders = (timezone?: string): void => {
	timezoneHolder.setUserTimezone(timezone);
	platformTimezoneHolder.setUserTimezone(timezone);
};

const toCurrentUser = (response: UserResponse) => ({
	name: response.name,
	accountId: response.account_id,
	email: response.email,
	locale: response.locale,
	picture: response.picture,
	timeZone: response.zoneinfo,
	jobTitle: response.extended_profile?.job_title,
});

const unresolvedUser: User = {
	accountId: 'failed-to-fetch',
	locale: 'en',
};

export const userStore: Store<
	User,
	{
		initUser: (user: UserResponse) => ({ setState }: StoreActionApi<User>) => Promise<{
			name: string;
			accountId: string;
			email: string | undefined;
			locale: string | undefined;
			picture: string;
			timeZone: string | undefined;
			jobTitle: string | undefined;
		}>;
		initCurrentUser: () => ({ setState }: StoreActionApi<User>) => Promise<User>;
		setUserLocale: (locale: any) => ({ setState }: StoreActionApi<User>) => Promise<void>;
	}
> = createStore({
	initialState: {} as User,
	actions: {
		initUser:
			(
				user: UserResponse,
			): (({ setState }: StoreActionApi<User>) => Promise<{
				name: string;
				accountId: string;
				email: string | undefined;
				locale: string | undefined;
				picture: string;
				timeZone: string | undefined;
				jobTitle: string | undefined;
			}>) =>
			async ({ setState }) => {
				const newState = toCurrentUser(user);
				syncUserTimezoneToHolders(user.zoneinfo);
				setState(newState);
				return newState;
			},
		initCurrentUser:
			(): (({ setState }: StoreActionApi<User>) => Promise<User>) =>
			async ({ setState }) => {
				try {
					const user = await identityClient.getUser();
					const newState = {
						...toCurrentUser(user),
					};
					syncUserTimezoneToHolders(user.zoneinfo);
					setState(newState);
					return newState;
				} catch (error) {
					console.error(`Failed to initCurrentUser in userStore due to: ${error}`);
					return unresolvedUser;
				}
			},
		setUserLocale:
			(locale: any): (({ setState }: StoreActionApi<User>) => Promise<void>) =>
			async ({ setState }) => {
				setState({ locale });
			},
	},
	name: 'user-store',
});
